import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  TrustAdvisorLevel,
  TrusteeDutyKind,
  TrusteeLevel,
} from '@/types/schema';

/** TRUSTEE DUTIES */
export const TRUSTEE_DUTY_DISPLAY_NAMES: Record<TrusteeDutyKind, string> = {
  ADMINISTRATION: 'Administration',
  DISTRIBUTION: 'Distribution',
  INVESTMENT: 'Investment',
  SPECIAL: 'Other',
};

export const TRUSTEE_DUTY_ABBREVIATIONS: Partial<
  Record<TrusteeDutyKind, string>
> = {
  ADMINISTRATION: 'Admin.',
  DISTRIBUTION: 'Dist.',
  INVESTMENT: 'Inv.',
};

export const TRUSTEE_DUTY_KIND_ITEMS: SelectInputOption<TrusteeDutyKind>[] = [
  TrusteeDutyKind.Investment,
  TrusteeDutyKind.Administration,
  TrusteeDutyKind.Distribution,
  TrusteeDutyKind.Special,
].map((value) => ({
  display: TRUSTEE_DUTY_DISPLAY_NAMES[value],
  abbreviation: TRUSTEE_DUTY_ABBREVIATIONS[value],
  value,
}));

/** ORDERED LEVELS */
export const ORDERED_TRUSTEE_LEVELS: TrusteeLevel[] = [
  TrusteeLevel.Primary,
  TrusteeLevel.Secondary,
  TrusteeLevel.Tertiary,
  TrusteeLevel.Quaternary,
  TrusteeLevel.Quinary,
  TrusteeLevel.Other,
];

export const ORDERED_TRUST_ADVISOR_LEVELS: TrustAdvisorLevel[] = [
  TrustAdvisorLevel.Primary,
  TrustAdvisorLevel.Secondary,
  TrustAdvisorLevel.Tertiary,
  TrustAdvisorLevel.Other,
];

// Returns the position of `level` in `ORDERED_TRUSTEE_LEVELS`. Null/missing
// or unrecognized levels sort to the end (Infinity).
export function trusteeLevelIndex(
  level: TrusteeLevel | null | undefined
): number {
  if (!level) return Infinity;
  const idx = ORDERED_TRUSTEE_LEVELS.indexOf(level);
  return idx === -1 ? Infinity : idx;
}

// Returns the position of `level` in `ORDERED_TRUST_ADVISOR_LEVELS`.
// Null/missing or unrecognized levels sort to the end (Infinity).
export function trustAdvisorLevelIndex(
  level: TrustAdvisorLevel | null | undefined
): number {
  if (!level) return Infinity;
  const idx = ORDERED_TRUST_ADVISOR_LEVELS.indexOf(level);
  return idx === -1 ? Infinity : idx;
}

/** TRUSTEE LEVELS */
// while successor trustees and "normal" trustees are the same type in the schema, and therefore they both
// can have defined "levels", we don't actually collect level information for primary trustee, and so these
// level display names are specific to successor trustees
export const SUCCESSOR_TRUSTEE_LEVEL_GROUP_DISPLAY_NAMES: Record<
  TrusteeLevel,
  string
> = {
  PRIMARY: 'First successor trustees',
  SECONDARY: 'Second successor trustees',
  TERTIARY: 'Third successor trustees',
  QUATERNARY: 'Fourth successor trustees',
  QUINARY: 'Fifth successor trustees',
  OTHER: 'Other successor trustees',
};

export const SUCCESSOR_TRUSTEE_LEVEL_GROUP_DISPLAY_NAMES_SHORT: Record<
  TrusteeLevel,
  string
> = {
  PRIMARY: 'First successors',
  SECONDARY: 'Secondary successors',
  TERTIARY: 'Tertiary successors',
  QUATERNARY: 'Fourth successors',
  QUINARY: 'Fifth successors',
  OTHER: 'Other successors',
};

export const TRUSTEE_LEVEL_DISPLAY_NAMES: Record<TrusteeLevel, string> = {
  [TrusteeLevel.Primary]: 'First',
  [TrusteeLevel.Secondary]: 'Second',
  [TrusteeLevel.Tertiary]: 'Third',
  [TrusteeLevel.Quaternary]: 'Fourth',
  [TrusteeLevel.Quinary]: 'Fifth',
  [TrusteeLevel.Other]: 'Other',
};

export const TRUSTEE_LEVEL_ITEMS: SelectInputOption<TrusteeLevel>[] = [
  TrusteeLevel.Primary,
  TrusteeLevel.Secondary,
  TrusteeLevel.Tertiary,
  TrusteeLevel.Quaternary,
  TrusteeLevel.Quinary,
  TrusteeLevel.Other,
].map((value) => ({
  display: TRUSTEE_LEVEL_DISPLAY_NAMES[value],
  value,
}));

/** TRUST ADVISOR LEVELS */
export const TRUST_ADVISOR_LEVEL_GROUP_DISPLAY_NAMES: Record<
  TrustAdvisorLevel,
  string
> = {
  PRIMARY: 'Primary trust advisors',
  SECONDARY: 'Secondary trust advisors',
  TERTIARY: 'Tertiary trust advisors',
  OTHER: 'Other trust advisors',
};

export const TRUST_ADVISOR_LEVEL_GROUP_DISPLAY_NAMES_SHORT: Record<
  TrustAdvisorLevel,
  string
> = {
  PRIMARY: 'Primary',
  SECONDARY: 'Secondary',
  TERTIARY: 'Tertiary',
  OTHER: 'Other',
};
